import { useState } from "react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Plus } from "untitledui-js-base";
import { useMedia } from "shared/providers";
import { useAuthContext } from "providers/Auth";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Unit } from "types/landlord/address";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  Calendar03Icon,
  Door02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { TemplateLinksSubmenu } from "shared/components/TemplateLinksSubmenu/TemplateLinksSubmenu";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";

interface HeaderActionsSectionProps {
  unit?: Unit;
  onInviteApplicants: () => void;
}

export const HeaderActionsSection = ({
  unit,
  onInviteApplicants
}: HeaderActionsSectionProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { isMobile } = useMedia();
  const { type } = useAuthContext();
  const [pendingUnlistedLink, setPendingUnlistedLink] = useState<string | null>(
    null
  );

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleCopyClick = (link: string) => {
    if (!unit?.listed) {
      setPendingUnlistedLink(link);
      return;
    }
    copyLink(link);
  };

  const isDisabled = unit?.listed ? !unit?.magic_link?.active : false;
  const isRequestShowingEnabled =
    unit?.property.prescreening_template?.show_request_showing_button;
  const isRequestShowingDisabled = !unit?.listed || !isRequestShowingEnabled;
  const getRequestShowingTooltip = () => {
    if (!unit?.listed)
      return `You can't copy a link to a ${dictionary.unit} without listing it`;
    if (!isRequestShowingEnabled)
      return "Request showing is not enabled for this listing";
    return undefined;
  };

  if (!type.isBoomScreen) return null;

  return (
    <div className="w-full md:ml-auto py-4 md:py-0 flex items-center md:justify-end gap-2 flex-wrap md:flex-nowrap">
      <Show if={!isMobile}>
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          {type.isBoomCRM ? (
            <ContextMenuPopover
              placement="bottom-end"
              cbTrigger={(open) => (
                <Button
                  HugeIcon={open ? ArrowUp01Icon : ArrowDown01Icon}
                  size={ButtonSize.SM}
                  iconPosition="right"
                  className="md:ml-6 w-full sm:w-auto"
                >
                  Copy link
                </Button>
              )}
              actions={[
                {
                  title: (
                    <div className="flex gap-3 items-center py-1">
                      <HugeiconsIcon
                        icon={Door02Icon}
                        size={20}
                        stroke="1px"
                        className="text-content-200"
                      />
                      <p className="text-base text-content-1000 font-medium">
                        Copy {dictionary.unit} listing page
                      </p>
                    </div>
                  ),
                  onClick: () => {
                    handleCopyClick(unit?.listing_url || "");
                  },
                  disabled: !unit?.listed,
                  tooltip: !unit?.listed
                    ? `You can't copy a link to a ${dictionary.unit} without listing it`
                    : undefined
                },
                {
                  title: (
                    <TemplateLinksSubmenu
                      variant="FULL"
                      disabled={isDisabled}
                      onCopyLink={handleCopyClick}
                      defaultLink={unit?.magic_link?.application_link || ""}
                      unitId={unit?.id ? String(unit.id) : undefined}
                    />
                  ),
                  disabled: isDisabled,
                  tooltip: isDisabled
                    ? `When a ${dictionary.unit} is not accepting applications, applicants can only be invited via email`
                    : undefined,
                  onClick: (e) => {
                    e.stopPropagation();
                  }
                },
                {
                  title: (
                    <div className="flex gap-3 items-center py-1">
                      <HugeiconsIcon
                        icon={Calendar03Icon}
                        size={20}
                        stroke="1px"
                        className="text-content-200"
                      />
                      <p className="text-base text-content-1000 font-medium">
                        Copy request showing link
                      </p>
                    </div>
                  ),
                  onClick: () => {
                    handleCopyClick(
                      unit?.listing_url
                        ? `${unit.listing_url}/request-showing`
                        : ""
                    );
                  },
                  disabled: isRequestShowingDisabled,
                  tooltip: getRequestShowingTooltip()
                }
              ]}
            />
          ) : (
            <div className="md:ml-6 w-full sm:w-auto">
              <TemplateLinksSubmenu
                variant="BUTTON"
                disabled={isDisabled}
                onCopyLink={handleCopyClick}
                defaultLink={unit?.magic_link?.application_link || ""}
                unitId={unit?.id ? String(unit.id) : undefined}
              />
            </div>
          )}
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={onInviteApplicants}
            className="w-full sm:w-auto"
            Icon={Plus}
            tooltipPlacement="bottom-start"
          >
            Invite applicants
          </Button>
        </div>
      </Show>

      <ConfirmDialog
        opened={pendingUnlistedLink !== null}
        onClose={() => setPendingUnlistedLink(null)}
        onConfirm={() => {
          if (pendingUnlistedLink) copyLink(pendingUnlistedLink);
          setPendingUnlistedLink(null);
        }}
        title={`${dictionary.Unit} not listed`}
        description={`This ${dictionary.unit} isn't listed yet. The link you copied won't work for applicants — for internal use only.`}
        variant="primary"
        icon={{ icon: AlertCircleIcon, variant: "primary" }}
        confirmButton={{ label: "Copy link" }}
      />
    </div>
  );
};
